/**
 * Supplier Detail Page
 *
 * Displays detailed information about a specific supplier:
 * - Supplier information and contact details
 * - Halal certification status and expiry
 * - Certificate documents
 * - Products/ingredients supplied
 * - Actions (edit, deactivate, view certificate)
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Alert } from '../../components/common/Alert';
import { mockApi } from '../../services/mockApi';
import { formatDate } from '../../utils/formatting';
import type { Supplier } from '../../types/supplier';

export const SupplierDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['common', 'company']);
  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSupplier();
  }, [id]);

  const loadSupplier = async () => {
    if (!id) {
      setError('Supplier ID not provided');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await mockApi.getSupplier(id);
      if (response.success && response.data) {
        setSupplier(response.data);
      } else {
        setError(response.error || 'Supplier not found');
      }
    } catch (err) {
      console.error('Error loading supplier:', err);
      setError('Failed to load supplier');
    } finally {
      setLoading(false);
    }
  };

  const isExpiringSoon = (supplier: Supplier): boolean => {
    if (!supplier.halalCertified || !supplier.certificationExpiryDate) return false;
    const daysUntilExpiry = Math.ceil(
      (new Date(supplier.certificationExpiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiry <= 60 && daysUntilExpiry > 0;
  };

  const isExpired = (supplier: Supplier): boolean => {
    if (!supplier.halalCertified || !supplier.certificationExpiryDate) return false;
    return new Date(supplier.certificationExpiryDate).getTime() < new Date().getTime();
  };

  const getDaysUntilExpiry = (supplier: Supplier): number => {
    if (!supplier.certificationExpiryDate) return 0;
    return Math.ceil(
      (new Date(supplier.certificationExpiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
  };

  const handleEdit = () => {
    alert('Navigate to edit supplier page');
  };

  const handleViewCertificate = () => {
    alert('View halal certificate (open PDF)');
  };

  const handleDeactivate = () => {
    if (window.confirm(`Are you sure you want to deactivate ${supplier?.name}? This will prevent them from being used in new applications.`)) {
      alert('Supplier deactivated (mock action)');
    }
  };

  if (loading) {
    return (
      <PageLayout pageTitle="Loading...">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('common:status.loading')}...</div>
        </div>
      </PageLayout>
    );
  }

  if (error || !supplier) {
    return (
      <PageLayout pageTitle="Supplier Not Found">
        <Alert type="error" title="Error">
          {error || 'Supplier not found'}
        </Alert>
        <div className="mt-4">
          <Button variant="outline" onClick={() => alert('Navigate to Suppliers List')}>
            ← Back to Suppliers
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={supplier.name}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('company:dashboard.title'), href: '/company/dashboard' },
        { label: t('company:suppliers.title'), href: '/company/suppliers' },
        { label: supplier.name },
      ]}
      actions={
        <div className="flex gap-2">
          {supplier.halalCertified && supplier.certificateUrl && (
            <Button variant="outline" onClick={handleViewCertificate}>
              View Certificate
            </Button>
          )}
          <Button variant="outline" onClick={handleEdit}>
            {t('common:actions.edit')}
          </Button>
        </div>
      }
    >
      {/* Certification Alerts */}
      {isExpired(supplier) && (
        <Alert type="error" className="mb-6">
          This supplier's halal certificate has expired. Please request an updated certificate or find an alternative supplier.
        </Alert>
      )}

      {!isExpired(supplier) && isExpiringSoon(supplier) && (
        <Alert type="warning" className="mb-6">
          This supplier's halal certificate will expire in {getDaysUntilExpiry(supplier)} days on{' '}
          {formatDate(supplier.certificationExpiryDate!)}. Please request renewal documentation.
        </Alert>
      )}

      {!supplier.halalCertified && (
        <Alert type="warning" className="mb-6">
          This supplier is not halal certified. Products from this supplier may not be used in halal-certified production.
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Supplier Information */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Supplier Information</h3>
            </CardHeader>
            <CardBody>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Supplier Name</dt>
                  <dd className="mt-1 text-sm font-medium">{supplier.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Supplier Type</dt>
                  <dd className="mt-1">
                    <Badge variant="default">{supplier.type.replace(/_/g, ' ')}</Badge>
                  </dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Address</dt>
                  <dd className="mt-1 text-sm">
                    {supplier.address.street}
                    <br />
                    {supplier.address.city}, {supplier.address.state} {supplier.address.postalCode}
                    <br />
                    {supplier.address.country}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Primary Email</dt>
                  <dd className="mt-1 text-sm">
                    <a href={`mailto:${supplier.contactPerson.email}`} className="text-blue-600 hover:underline">
                      {supplier.contactPerson.email}
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Primary Phone</dt>
                  <dd className="mt-1 text-sm">
                    <a href={`tel:${supplier.contactPerson.phone}`} className="text-blue-600 hover:underline">
                      {supplier.contactPerson.phone}
                    </a>
                  </dd>
                </div>
                {supplier.website && (
                  <div className="md:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Website</dt>
                    <dd className="mt-1 text-sm">
                      <a
                        href={supplier.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {supplier.website}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </CardBody>
          </Card>

          {/* Contact Person */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Contact Person</h3>
            </CardHeader>
            <CardBody>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="mt-1 text-sm">{supplier.contactPerson.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Position</dt>
                  <dd className="mt-1 text-sm">{supplier.contactPerson.position}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm">
                    <a href={`mailto:${supplier.contactPerson.email}`} className="text-blue-600 hover:underline">
                      {supplier.contactPerson.email}
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="mt-1 text-sm">
                    <a href={`tel:${supplier.contactPerson.phone}`} className="text-blue-600 hover:underline">
                      {supplier.contactPerson.phone}
                    </a>
                  </dd>
                </div>
              </dl>
            </CardBody>
          </Card>

          {/* Halal Certification Details */}
          {supplier.halalCertified && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Halal Certification Details</h3>
              </CardHeader>
              <CardBody>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Certificate Number</dt>
                    <dd className="mt-1 text-sm font-medium">{supplier.certificationNumber}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Certifying Body</dt>
                    <dd className="mt-1 text-sm">{supplier.certificationBody}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Issue Date</dt>
                    <dd className="mt-1 text-sm">
                      {supplier.certificationIssueDate
                        ? formatDate(supplier.certificationIssueDate)
                        : 'Not specified'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Expiry Date</dt>
                    <dd
                      className={`mt-1 text-sm ${
                        isExpired(supplier)
                          ? 'text-red-600 font-medium'
                          : isExpiringSoon(supplier)
                          ? 'text-orange-600 font-medium'
                          : ''
                      }`}
                    >
                      {supplier.certificationExpiryDate ? (
                        <div>
                          {formatDate(supplier.certificationExpiryDate)}
                          {isExpired(supplier) && <span className="ml-2">(Expired)</span>}
                          {!isExpired(supplier) && isExpiringSoon(supplier) && (
                            <div className="text-xs mt-1">({getDaysUntilExpiry(supplier)} days remaining)</div>
                          )}
                        </div>
                      ) : (
                        'Not specified'
                      )}
                    </dd>
                  </div>
                </dl>

                {supplier.certificateUrl && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <Button variant="outline" onClick={handleViewCertificate} icon={<FileText className="w-4 h-4" />}>
                      View Certificate Document
                    </Button>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Products/Services Provided */}
          {supplier.productsProvided && supplier.productsProvided.length > 0 && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">Products/Services Provided</h3>
              </CardHeader>
              <CardBody>
                <div className="flex flex-wrap gap-2">
                  {supplier.productsProvided.map((product, index) => (
                    <Badge key={index} variant="default">
                      {product}
                    </Badge>
                  ))}
                </div>
                {supplier.materialCategories && supplier.materialCategories.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm font-medium text-gray-500 mb-2">Material Categories</div>
                    <div className="flex flex-wrap gap-2">
                      {supplier.materialCategories.map((category, index) => (
                        <Badge key={index} variant="default" size="sm">
                          {category}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </div>

        {/* Sidebar - Right Column */}
        <div className="space-y-6">
          {/* Quick Status */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Status</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-3">
                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Halal Certification</div>
                  {supplier.halalCertified ? (
                    <div>
                      <Badge
                        variant={isExpired(supplier) ? 'error' : isExpiringSoon(supplier) ? 'warning' : 'success'}
                        size="sm"
                      >
                        {isExpired(supplier)
                          ? 'Expired'
                          : isExpiringSoon(supplier)
                          ? 'Expiring Soon'
                          : 'Certified'}
                      </Badge>
                    </div>
                  ) : (
                    <Badge variant="default" size="sm">
                      Not Certified
                    </Badge>
                  )}
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Supplier Status</div>
                  <Badge variant={supplier.status === 'ACTIVE' ? 'success' : 'default'} size="sm">
                    {supplier.status.replace(/_/g, ' ')}
                  </Badge>
                </div>

                {supplier.riskLevel && (
                  <div>
                    <div className="text-sm font-medium text-gray-500 mb-1">Risk Level</div>
                    <Badge
                      variant={
                        supplier.riskLevel === 'LOW'
                          ? 'success'
                          : supplier.riskLevel === 'MEDIUM'
                          ? 'warning'
                          : 'error'
                      }
                      size="sm"
                    >
                      {supplier.riskLevel}
                    </Badge>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Actions</h3>
            </CardHeader>
            <CardBody>
              <div className="space-y-2">
                <Button variant="outline" fullWidth onClick={handleEdit}>
                  {t('common:actions.edit')} Supplier
                </Button>
                {supplier.halalCertified && supplier.certificateUrl && (
                  <Button variant="outline" fullWidth onClick={handleViewCertificate}>
                    View Certificate
                  </Button>
                )}
                <Button variant="outline" fullWidth onClick={() => alert('Contact supplier')}>
                  Contact Supplier
                </Button>
                <Button variant="outline" fullWidth onClick={() => alert('View order history')}>
                  View Order History
                </Button>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={handleDeactivate}
                  className="text-red-600 hover:text-red-700"
                >
                  {supplier.status === 'ACTIVE' ? 'Deactivate' : 'Activate'} Supplier
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Timeline</h3>
            </CardHeader>
            <CardBody>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="font-medium text-gray-500">Added</dt>
                  <dd className="mt-1">{formatDate(supplier.createdAt)}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Last Updated</dt>
                  <dd className="mt-1">{formatDate(supplier.updatedAt)}</dd>
                </div>
                {supplier.lastAuditDate && (
                  <div>
                    <dt className="font-medium text-gray-500">Last Audit</dt>
                    <dd className="mt-1">{formatDate(supplier.lastAuditDate)}</dd>
                  </div>
                )}
                {supplier.nextAuditDate && (
                  <div>
                    <dt className="font-medium text-gray-500">Next Audit</dt>
                    <dd className="mt-1">{formatDate(supplier.nextAuditDate)}</dd>
                  </div>
                )}
              </dl>
            </CardBody>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default SupplierDetailPage;

